<template>
    <div
        ref="rootRef"
        class="tw:relative"
        data-testid="header-notifications"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <button
            type="button"
            class="admin-header-bell-hover tw:group tw:relative tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-slate-200 tw:bg-white tw:p-2 tw:text-slate-800 tw:transition tw:hover:border-slate-300 tw:hover:bg-slate-50"
            :class="{ 'admin-header-bell-ring': ringing }"
            aria-label="Notificaciones"
            :aria-expanded="open ? 'true' : 'false'"
            data-testid="header-notifications-toggle"
            @click="toggle"
        >
            <i class="admin-header-icon fas fa-bell tw:text-[15px]" aria-hidden="true"></i>
            <span
                v-if="unreadCount > 0"
                :key="unreadCount"
                class="tw:absolute tw:-top-1.5 tw:-right-1.5 tw:flex tw:items-center tw:justify-center tw:min-w-[16px] tw:h-4 tw:px-1 tw:rounded-full tw:text-[10px] tw:font-bold admin-header-badge-pop"
                :style="{ backgroundColor: 'var(--admin-club-color)', color: 'var(--admin-club-fg)' }"
                data-testid="header-notifications-badge"
            >{{ unreadCount }}</span>
        </button>

        <div
            v-show="open"
            class="tw:fixed tw:left-4 tw:right-4 tw:top-[72px] tw:z-50 tw:w-auto tw:max-w-none tw:rounded-xl tw:border tw:border-slate-200 tw:bg-white tw:shadow-xl tw:text-slate-900 tw:md:absolute tw:md:left-auto tw:md:right-0 tw:md:top-full tw:md:mt-2 tw:md:w-96 tw:md:max-w-[calc(100vw-2rem)]"
            data-testid="header-notifications-panel"
        >
            <div class="tw:flex tw:items-center tw:justify-between tw:border-b tw:border-slate-100 tw:px-4 tw:py-3">
                <span class="tw:text-sm tw:font-semibold">Notificaciones</span>
                <span v-if="unreadCount > 0" class="tw:text-[11px] tw:font-medium tw:text-slate-500">{{ unreadCount }} sin leer</span>
            </div>

            <div class="tw:max-h-[340px] tw:overflow-y-auto">
                <div v-if="notificaciones.length === 0" class="tw:py-6 tw:text-center tw:text-sm tw:text-slate-500" data-testid="header-notifications-empty">
                    No tienes notificaciones.
                </div>

                <template v-else>
                    <button
                        v-for="notificacion in notificaciones"
                        :key="notificationKey(notificacion)"
                        type="button"
                        class="tw:flex tw:w-full tw:items-start tw:gap-2.5 tw:border-b tw:border-slate-100 tw:px-4 tw:py-3 tw:text-left tw:transition-colors tw:hover:bg-slate-50"
                        :data-testid="'header-notification-' + notificacion.id"
                        @click="marcarComoLeida(notificacion)"
                    >
                        <i class="fas fa-bell tw:mt-0.5 tw:shrink-0 tw:text-xs tw:text-slate-400" aria-hidden="true"></i>
                        <span class="tw:min-w-0">
                            <span class="tw:block tw:text-xs tw:leading-snug tw:text-slate-700" v-html="sanitize(notificacion.data && notificacion.data.mensaje)"></span>
                            <span class="tw:mt-0.5 tw:block tw:text-[10px] tw:font-medium tw:text-rose-500">{{ formatNotificacionFecha(notificacion.created_at) }}</span>
                        </span>
                    </button>
                </template>
            </div>

            <div v-if="unreadCount > 0" class="tw:border-t tw:border-slate-100 tw:p-2">
                <button
                    type="button"
                    class="tw:w-full tw:rounded-lg tw:px-4 tw:py-2 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-slate-500 tw:transition-colors tw:hover:bg-slate-100 tw:hover:text-slate-800"
                    data-testid="header-notifications-mark-all"
                    @click="marcarComoLeidas"
                >Marcar como leídas</button>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, watch } from 'vue';
    import { useAdminNotifications, formatNotificacionFecha } from '@/composables/admin/layout/useAdminNotifications';
    import { useHeaderDropdown } from '@/composables/admin/layout/useHeaderDropdown';
    import { useNotificationBellRing } from '@/composables/admin/layout/useNotificationBellRing';

    const {
        notificaciones,
        unreadCount,
        nuevasNotificaciones,
        marcarComoLeida,
        marcarComoLeidas,
        sanitize,
        refreshNotificaciones
    } = useAdminNotifications();

    const rootRef = ref(null);
    const { open, toggle, onMouseEnter, onMouseLeave } = useHeaderDropdown(rootRef, { hover: true });

    // La campana se balancea al llegar algo nuevo, salvo que el panel esté
    // abierto y el usuario ya lo esté viendo.
    const { ringing } = useNotificationBellRing(open, nuevasNotificaciones);

    // El header no se remonta al navegar: al abrir la campana se refresca el
    // contador (el composable aplica un cooldown para no repetir en ráfaga).
    watch(open, isOpen => {
        if (isOpen) refreshNotificaciones();
    });

    /**
     * Backend y comunicados pueden compartir id numérico; prefijar la clave
     * evita el aviso de claves duplicadas de Vue.
     */
    function notificationKey(notificacion) {
        return (notificacion.tipo === 'comunicados' ? 'com-' : 'back-') + notificacion.id;
    }
</script>

<style scoped>
    /*
     * Campana: se hamaca al hover (rotación con origen en el anclaje
     * superior), en lugar del scale-up anterior. Réplica del gesto
     * 0 / -10 / 10 / -10 / 0 del icono animado de referencia.
     */
    .admin-header-bell-hover:hover .admin-header-icon,
    .admin-header-bell-ring .admin-header-icon {
        animation: adminBellRing 0.5s ease-in-out;
        transform-origin: 50% 0;
    }

    @keyframes adminBellRing {
        0% {
            transform: rotate(0);
        }
        25% {
            transform: rotate(-10deg);
        }
        50% {
            transform: rotate(10deg);
        }
        75% {
            transform: rotate(-10deg);
        }
        100% {
            transform: rotate(0);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .admin-header-bell-hover:hover .admin-header-icon,
        .admin-header-bell-ring .admin-header-icon {
            animation: none;
        }
    }

    .admin-header-badge-pop {
        animation: adminHeaderBadgePop 0.25s ease;
    }

    @keyframes adminHeaderBadgePop {
        0% {
            transform: scale(0.6);
        }
        100% {
            transform: scale(1);
        }
    }
</style>
